import React from "react";

/**
 * EmployeeEntryTable
 * ------------------
 * Used inside:
 *  - Monthly / Weekly (period expand)
 *  - History by Employee
 *  - History by Date
 *
 * Expects:
 *   entries[] where each entry may have:
 *   - direct columns
 *   - raw_json fallback (AI extracted data)
 */

export default function EmployeeEntryTable({ entries = [] }) {
  const totals = entries.reduce(
    (acc, e) => {
      const r = e.raw_json || {};

      acc.gross_pay += Number(e.gross_pay ?? r.gross_pay ?? 0);
      acc.taxable_gross += Number(r.taxable_gross ?? 0);
      acc.nic_able_gross += Number(r.nic_able_gross ?? 0);

      acc.tax += Number(e.tax ?? r.tax ?? 0);
      acc.employee_nics += Number(e.employee_nics ?? r.employee_nics ?? 0);
      acc.student_loans += Number(
        (r.student_loan_deductions ?? 0) +
        (r.postgrad_loan_deductions ?? 0)
      );

      acc.net_pay += Number(e.net_pay ?? r.net_pay ?? 0);
      acc.take_home_pay += Number(e.take_home_pay ?? r.take_home_pay ?? 0);

      acc.employer_nics += Number(e.employer_nics ?? r.employer_nics ?? 0);
      acc.employer_pension += Number(r.employer_pension ?? 0);
      acc.cost_to_employer += Number(
        e.cost_to_employer ?? r.cost_to_employer ?? 0
      );

      return acc;
    },
    {
      gross_pay: 0,
      taxable_gross: 0,
      nic_able_gross: 0,
      tax: 0,
      employee_nics: 0,
      student_loans: 0,
      net_pay: 0,
      take_home_pay: 0,
      employer_nics: 0,
      employer_pension: 0,
      cost_to_employer: 0,
    }
  );
  return (
    <div className="overflow-x-auto">
      <table
        className="table table-sm table-bordered bg-white mb-0"
        style={{ minWidth: "1600px" }}
      >
        <thead className="table-secondary sticky top-0 z-10">
          <tr>
            <Th w={120}>Date</Th>
            {/* <Th w={120}>Employee Ref</Th> */}
            <Th w={220}>Employee Name</Th>

            <Th w={120} align="right">Gross Pay</Th>
            <Th w={140} align="right">Taxable Gross</Th>
            <Th w={140} align="right">NIC-able Gross</Th>

            <Th w={110} align="right">Tax</Th>
            <Th w={140} align="right">Employee NICs</Th>
            <Th w={180} align="right">Student / Postgrad Loan</Th>

            <Th w={130} align="right">Net Pay</Th>
            <Th w={150} align="right">Take-home Pay</Th>

            <Th w={130} align="right">Employer NICs</Th>
            <Th w={150} align="right">Employer Pension</Th>

            <Th w={150} align="right">Cost to Employer</Th>
          </tr>
        </thead>

        <tbody>
          {entries.length === 0 && (
            <tr>
              <td colSpan={14} className="text-center py-3 text-muted">
                No entries found
              </td>
            </tr>
          )}

          {entries.map((e, i) => {
            const r = e.raw_json || {};

            return (
              <tr key={e.id || i} className="hover:bg-gray-50">
                <Td>{formatDate(e.date || e.entry_date || r.period_end_date)}</Td>
                {/* <Td>{e.employee_ref ?? r.employee_ref ?? "-"}</Td> */}
                <Td>{e.employee_name ?? r.employee_name ?? "-"}</Td>

                <Td align="right">{money(e.gross_pay ?? r.gross_pay)}</Td>
                <Td align="right">{money(r.taxable_gross)}</Td>
                <Td align="right">{money(r.nic_able_gross)}</Td>

                <Td align="right">{money(e.tax ?? r.tax)}</Td>
                <Td align="right">{money(e.employee_nics ?? r.employee_nics)}</Td>

                <Td align="right">
                  {money(
                    r.student_loan_deductions ??
                    r.postgrad_loan_deductions
                  )}
                </Td>

                <Td align="right">{money(e.net_pay ?? r.net_pay)}</Td>
                <Td align="right">{money(e.take_home_pay ?? r.take_home_pay)}</Td>

                <Td align="right">{money(e.employer_nics ?? r.employer_nics)}</Td>
                <Td align="right">{money(r.employer_pension)}</Td>

                <Td align="right">
                  {money(e.cost_to_employer ?? r.cost_to_employer)}
                </Td>
              </tr>
            );
          })}
        </tbody>
        {entries.length > 0 && (
          <tfoot className="table-light fw-semibold">
            <tr>
              <Td></Td>
              <Td align="right" colSpan={2}>
                TOTAL
              </Td>

              <Td align="right">{money(totals.gross_pay)}</Td>
              <Td align="right">{money(totals.taxable_gross)}</Td>
              <Td align="right">{money(totals.nic_able_gross)}</Td>
              <Td align="right">{money(totals.tax)}</Td>
              <Td align="right">{money(totals.employee_nics)}</Td>
              <Td align="right">{money(totals.student_loans)}</Td>
              <Td align="right">{money(totals.net_pay)}</Td>
              <Td align="right">{money(totals.take_home_pay)}</Td>
              <Td align="right">{money(totals.employer_nics)}</Td>
              <Td align="right">{money(totals.employer_pension)}</Td>
              <Td align="right">{money(totals.cost_to_employer)}</Td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/* ====================== HELPERS ====================== */

const Th = ({ children, w, align = "left" }) => (
  <th
    style={{ minWidth: w }}
    className={`px-2 py-2 border text-${align} whitespace-nowrap`}
  >
    {children}
  </th>
);

const Td = ({ children, align = "left" }) => (
  <td className={`px-2 py-1 border text-${align} whitespace-nowrap`}>
    {children}
  </td>
);

function money(v) {
  if (v === null || v === undefined || isNaN(Number(v))) return "-";
  return `£${Number(v).toFixed(2)}`;
}

function formatDate(d) {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("en-GB");
  } catch (e) {
    return d;
  }
}
